/**
 * Connector type registry metadata (#445).
 *
 * Future types extend this table/list via metadata — clients must not hardcode
 * UI branches per type. Built-ins seed the registry; unknown types stay usable
 * when registered server-side with metadata.
 */

import type {
  ConnectorCapabilityFlags,
  ConnectorConnectionMode,
  ConnectorTypeKey,
  ConnectorTypeMetadata,
} from './types.js';
import { BUILTIN_CONNECTOR_TYPES, isBuiltinConnectorType } from './types.js';

const AI_CAPS: ConnectorCapabilityFlags = {
  list: true,
  connect: true,
  revoke: true,
  refresh: true,
  usage: true,
  health: true,
  runtimeControl: true,
};

const SUPABASE_CAPS: ConnectorCapabilityFlags = {
  list: true,
  connect: true,
  revoke: true,
  refresh: true,
  usage: false,
  health: true,
  runtimeControl: false,
};

const READ_ONLY_SYSTEM_CAPS: ConnectorCapabilityFlags = {
  list: true,
  connect: false,
  revoke: false,
  refresh: false,
  usage: false,
  health: true,
  runtimeControl: false,
};

/** Built-in registry rows. Mirror of `brunel.connector_type_registry` seed. */
export const BUILTIN_CONNECTOR_TYPE_REGISTRY: readonly ConnectorTypeMetadata[] = [
  {
    typeKey: 'ai_provider',
    displayLabel: 'AI provider / runtime',
    category: 'ai',
    supportsVault: true,
    supportsNodeBound: true,
    defaultConnectionMode: 'node_bound',
    capabilities: AI_CAPS,
    metadata: {
      bridgesAiConnections: true,
      noImplicitMeteredFallback: true,
    },
  },
  {
    typeKey: 'supabase_project',
    displayLabel: 'Supabase project / account',
    category: 'data',
    supportsVault: true,
    supportsNodeBound: false,
    defaultConnectionMode: 'unavailable',
    capabilities: SUPABASE_CAPS,
    metadata: {
      forbidDbLoginHarvest: true,
      managementApiOnly: true,
      requiresSecureVault: true,
    },
  },
  {
    typeKey: 'github_repository',
    displayLabel: 'GitHub repository',
    category: 'integration',
    supportsVault: true,
    supportsNodeBound: true,
    defaultConnectionMode: 'unavailable',
    capabilities: READ_ONLY_SYSTEM_CAPS,
    metadata: {
      readOnlyMonitoring: true,
      serverSideEvidenceOnly: true,
    },
  },
  {
    typeKey: 'cloudflare_pages',
    displayLabel: 'Cloudflare Pages project',
    category: 'integration',
    supportsVault: true,
    supportsNodeBound: true,
    defaultConnectionMode: 'unavailable',
    capabilities: READ_ONLY_SYSTEM_CAPS,
    metadata: {
      readOnlyMonitoring: true,
      noInfrastructureWrites: true,
      serverSideEvidenceOnly: true,
    },
  },
] as const;

const BY_KEY = new Map(
  BUILTIN_CONNECTOR_TYPE_REGISTRY.map((row) => [row.typeKey, row] as const),
);

export function connectorTypeMetadata(typeKey: string): ConnectorTypeMetadata | null {
  return BY_KEY.get(typeKey as ConnectorTypeKey) ?? null;
}

export function listBuiltinConnectorTypes(): readonly ConnectorTypeMetadata[] {
  return BUILTIN_CONNECTOR_TYPE_REGISTRY;
}

export function defaultModeForConnectorType(typeKey: string): ConnectorConnectionMode {
  const meta = connectorTypeMetadata(typeKey);
  if (meta) return meta.defaultConnectionMode;
  return 'unavailable';
}

/**
 * Secret-bearing modes stay dark until vault/KMS exists.
 * commandos_vault is never the live default today.
 */
export function resolveRegisterConnectionMode(input: {
  readonly connectorType: string;
  readonly requestedMode?: string | null;
  readonly aiConnectionKey?: string | null;
  readonly vaultAvailable?: boolean;
}): { readonly ok: true; readonly mode: ConnectorConnectionMode } | { readonly ok: false; readonly reason: string } {
  const vaultAvailable = input.vaultAvailable === true;
  const requested = input.requestedMode ?? null;

  if (requested === 'commandos_vault' && !vaultAvailable) {
    return { ok: false, reason: 'commandos_vault unavailable without KMS' };
  }

  if (input.connectorType === 'ai_provider') {
    if (input.aiConnectionKey === 'ollama-local') {
      return { ok: true, mode: 'local_no_auth' };
    }
    if (requested === 'local_no_auth' && input.aiConnectionKey !== 'ollama-local') {
      return { ok: false, reason: 'local_no_auth only for ollama-local' };
    }
    if (requested === 'unavailable') return { ok: true, mode: 'unavailable' };
    if (requested === 'node_bound' || requested === null) return { ok: true, mode: 'node_bound' };
    if (requested === 'commandos_vault' && vaultAvailable) return { ok: true, mode: 'commandos_vault' };
    return { ok: false, reason: 'unsupported AI connection mode' };
  }

  if (input.connectorType === 'supabase_project') {
    // Management tokens need vault; without it the connector is metadata-only / unavailable.
    if (!vaultAvailable) return { ok: true, mode: 'unavailable' };
    if (requested === 'commandos_vault' || requested === null) return { ok: true, mode: 'commandos_vault' };
    return { ok: false, reason: 'supabase connectors require vault when secrets are attached' };
  }

  if (input.connectorType === 'github_repository' || input.connectorType === 'cloudflare_pages') {
    if (requested === 'node_bound') return { ok: true, mode: 'node_bound' };
    if (requested === 'commandos_vault' && vaultAvailable) return { ok: true, mode: 'commandos_vault' };
    if (requested === 'unavailable' || requested === null) return { ok: true, mode: 'unavailable' };
    return { ok: false, reason: 'unsupported system monitoring connection mode' };
  }

  // Future registered types: default unavailable until vault or node_bound declared in metadata.
  if (!isBuiltinConnectorType(input.connectorType)) {
    return { ok: true, mode: defaultModeForConnectorType(input.connectorType) };
  }

  return { ok: false, reason: 'unknown connector type' };
}

export function knownBuiltinTypeKeys(): readonly string[] {
  return BUILTIN_CONNECTOR_TYPES;
}
